import React, { useContext, useEffect, useRef, useState } from "react"
import AdHocCommands from "@/api/adHocCommands"
import { XmppContext } from "@/stream/xmppClient"
import XMPPError from "@xmpp/error"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { GPS_PROVIDER_NAMES } from "@/lib/gpsProviderCredentials"
import Loader from "@/components/Loader"
import { useTranslation } from "react-i18next"

interface AddGpsProviderModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const isPasswordLike = (key: string) =>
  /password|secret|token|key|apikey|api_key|pw/i.test(key)

const sortCredentialFields = (fields: { var: string; label: string }[]) =>
  [...fields].sort(
    (a, b) => Number(isPasswordLike(a.var)) - Number(isPasswordLike(b.var)),
  )

const AddGpsProviderModal: React.FC<AddGpsProviderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)

  const [provider, setProvider] = useState("")
  const [name, setName] = useState("")

  const [sessionid, setSessionid] = useState("")
  const [credentialFields, setCredentialFields] = useState<
    { var: string; label: string }[]
  >([])
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const [loadingFields, setLoadingFields] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Debounce ref so rapid name typing doesn't fire multiple step-1 calls
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Fire step 1 whenever provider + name are both set
  useEffect(() => {
    setCredentialFields([])
    setCredentials({})
    setSessionid("")
    setFieldError(null)

    if (!provider || !name.trim()) return

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setLoadingFields(true)
      AdHocCommands.initGpsProviderCreate(client, provider, name.trim())
        .then(({ sessionid: sid, credentialFields: fields }) => {
          setSessionid(sid)
          setCredentialFields(fields)
        })
        .catch((err) => {
          // "" means no server text: the fallback is translated where shown.
          setFieldError(err instanceof XMPPError && err.text ? err.text : "")
        })
        .finally(() => setLoadingFields(false))
    }, 400)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [provider, name, client])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const missing = credentialFields.filter((f) => !credentials[f.var]?.trim())
    if (missing.length > 0) {
      setSubmitError(
        t("fleet.gpsProviderForm.missingFields", {
          fields: missing.map((f) => f.label).join(", "),
        }),
      )
      return
    }

    setSubmitting(true)
    setSubmitError(null)
    try {
      await AdHocCommands.submitGpsProviderCreate(
        client,
        sessionid,
        credentials,
      )
      onSuccess()
      handleClose()
    } catch (err) {
      setSubmitError(
        err instanceof XMPPError && err.text
          ? err.text
          : t("fleet.gpsProviderForm.createFailed"),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setProvider("")
    setName("")
    setSessionid("")
    setCredentialFields([])
    setCredentials({})
    setLoadingFields(false)
    setFieldError(null)
    setSubmitError(null)
    onClose()
  }

  const canSubmit = sessionid && credentialFields.length > 0 && !loadingFields

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("fleet.gpsProviderForm.addTitle")}</DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-1">
              <Label htmlFor="gps-provider-name">
                {t("fleet.gpsProviderForm.name")}
              </Label>
              <Input
                id="gps-provider-name"
                type="text"
                autoComplete="off"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("fleet.gpsProviderForm.namePlaceholder")}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="gps-provider-select">
                {t("fleet.gpsProviderForm.provider")}
              </Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger id="gps-provider-select">
                  <SelectValue
                    placeholder={t("fleet.gpsProviderForm.selectProvider")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {GPS_PROVIDER_NAMES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loadingFields && (
              <div className="flex justify-center py-2">
                <Loader />
              </div>
            )}

            {fieldError !== null && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {fieldError || t("fleet.gpsProviderForm.loadFieldsFailed")}
              </p>
            )}

            {!loadingFields &&
              sortCredentialFields(credentialFields).map((field) => (
                <div key={field.var} className="space-y-1">
                  <Label htmlFor={`cred-${field.var}`}>{field.label}</Label>
                  <Input
                    id={`cred-${field.var}`}
                    type={isPasswordLike(field.var) ? "password" : "text"}
                    autoComplete="off"
                    value={credentials[field.var] ?? ""}
                    onChange={(e) =>
                      setCredentials((prev) => ({
                        ...prev,
                        [field.var]: e.target.value,
                      }))
                    }
                    placeholder={field.label}
                  />
                </div>
              ))}

            {submitError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {submitError}
              </p>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={submitting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={submitting || !canSubmit}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting
                ? t("fleet.gpsProviderForm.connecting")
                : t("fleet.gps.addProvider")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddGpsProviderModal
